import { apiUrl, chainUrl, BACKEND_ORIGIN } from '../config'

const LEGACY_ROSE_HOSTS = new Set(['onlyrose-nft.hausserver.xyz'])

export function canonicalRoseUrl(value: unknown): unknown {
  if (typeof value !== 'string' || !value) return value
  let url: URL
  try { url = new URL(value) } catch { return value }
  if (!LEGACY_ROSE_HOSTS.has(url.hostname.toLowerCase())) return value
  if (url.pathname.startsWith('/api/')) return apiUrl(url.pathname + url.search + url.hash)
  if (url.pathname.startsWith('/chain-builder/')) return chainUrl(url.pathname + url.search + url.hash)
  return `${BACKEND_ORIGIN}${url.pathname}${url.search}${url.hash}`
}

export function canonicalizeRoseData<T>(value: T, seen = new WeakSet<object>()): T {
  if (typeof value === 'string') return canonicalRoseUrl(value) as T
  if (!value || typeof value !== 'object') return value
  if (seen.has(value as object)) return value
  seen.add(value as object)
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i += 1) value[i] = canonicalizeRoseData(value[i], seen)
    return value
  }
  for (const key of Object.keys(value as object)) {
    ;(value as any)[key] = canonicalizeRoseData((value as any)[key], seen)
  }
  return value
}

export async function request<T = any>(path: string, init: RequestInit = {}): Promise<T> {
  const url = path.startsWith('/chain-builder/') ? chainUrl(path) : path.startsWith('/api/') ? apiUrl(path) : path
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData
  const response = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      ...(isForm || init.body instanceof Blob ? {} : init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {})
    }
  })
  const type = String(response.headers.get('content-type') || '')
  const body: any = type.includes('json') ? await response.json().catch(() => ({})) : await response.text()
  if (!response.ok) {
    const detail = body?.detail
    const message = typeof detail === 'string' ? detail : detail?.message || body?.message || body?.error || `Request failed (${response.status})`
    throw new Error(message)
  }
  return canonicalizeRoseData(body) as T
}

export const get = <T = any>(path: string, init: RequestInit = {}) => request<T>(path, init)
export const post = <T = any>(path: string, body?: unknown, init: RequestInit = {}) => request<T>(path, { ...init, method: 'POST', body: body instanceof FormData || body instanceof Blob ? body : body === undefined ? undefined : JSON.stringify(body) })
export const patch = <T = any>(path: string, body?: unknown, init: RequestInit = {}) => request<T>(path, { ...init, method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) })
export const put = <T = any>(path: string, body?: unknown, init: RequestInit = {}) => request<T>(path, { ...init, method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) })
export const del = <T = any>(path: string, init: RequestInit = {}) => request<T>(path, { ...init, method: 'DELETE' })

export const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))
export const short = (value = '', head = 6, tail = 5) => value ? `${value.slice(0, head)}…${value.slice(-tail)}` : '—'
export const sol = (lamports: unknown) => lamports == null ? '—' : `${(Number(lamports) / 1e9).toLocaleString(undefined, { maximumFractionDigits: 6 })} SOL`
export const formatNumber = (value: unknown) => Number(value || 0).toLocaleString()
export const formatDate = (value: unknown) => value ? new Date(typeof value === 'number' ? value * 1000 : String(value)).toLocaleString() : '—'
